import {
  useState,
  useRef,
  useEffect } from 'react';
import {
  Button,
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  InputLabel,
  FormControl,
  Avatar,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText
} from '@mui/material';
import Select from '../components/SearchableSelect';
import { PhotoCamera, Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { getAllCategorie } from '../APIs/CategoriesAPI';
import { getAllSottocategorie } from '../APIs/SubCategoriesAPI';
import {
  creaProdotto,
  creaProdottoMultipart,
  aggiornaProdotto,
  aggiornaProdottoMultipart
} from '../APIs/ProductsAPI';
import { getConfiguredPrinters } from '../APIs/PrintersAPI';
import { getAllStanze } from '../APIs/RoomsAPI';
import { useTranslation } from 'react-i18next';
import TimePicker24Field from "../components/TimePicker24Field";
import { useSnackbar } from '../contexts/SnackBarContext';
import { BOARD_BASIS_OPTIONS, normalizeBoardBasis } from '../utils/boardBasis';

const defaultTimeSlot = () => ({
  from: new Date(new Date().setHours(0, 0, 0, 0)),
  to: new Date(new Date().setHours(23, 59, 0, 0)),
});

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto'
};

function normalizeSubcategoryEntries(subcategories = [], categories = []) {
  const byId = new Map();
  const addSubcategory = (subcategory, fallbackCategoryId = null) => {
    const id = Number(subcategory?.id);
    if (!Number.isInteger(id) || id <= 0) return;
    const categoryId = Number(
      subcategory?.categoryId ??
      subcategory?.category?.id ??
      fallbackCategoryId
    );
    byId.set(id, {
      ...subcategory,
      id,
      categoryId: Number.isInteger(categoryId) && categoryId > 0 ? categoryId : null,
    });
  };

  (subcategories || []).forEach((subcategory) => addSubcategory(subcategory));
  (categories || []).forEach((category) => {
    (category?.subcategories || []).forEach((subcategory) => addSubcategory(subcategory, category.id));
  });

  return Array.from(byId.values());
}

function ModalProdotto({ open = false, onClose, mode = 'create', product = null }) {
  const [id, setId] = useState(null);
  const [name, setNome] = useState('');
  const [prezzo, setPrezzo] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [subcategory, setSottocategoria] = useState('');
  const [timeSlots, setTimeSlots] = useState([defaultTimeSlot()]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [configuredPrinters, setConfiguredPrinters] = useState([]);
  const [selectedPrinters, setSelectedPrinters] = useState([]);
  const [roomPrinterTargets, setRoomPrinterTargets] = useState({});
  const [rooms, setRooms] = useState([]);
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState('');
  const [pricePerWeight, setPricePerWeight] = useState(false);
  const [typeOfWeight, setTypeOfWeight] = useState('');
  const [availableQuantity, setAvailableQuantity] = useState('');
  const [boardBasis, setBoardBasis] = useState('');
  const [active, setActive] = useState(true);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const tenantType = localStorage.getItem('tenantType');
  const isRestaurant = String(tenantType || '').toUpperCase() === 'RISTORANTE';
  const isConferenceTenant = String(tenantType || '').toUpperCase() === 'SALE_CONGRESSI';
  const weightEnum = { KILOGRAM: 'KILOGRAM', HECTOGRAM: 'HECTOGRAM' };
  const normalizedCategoryIds = (value) => {
    const raw = Array.isArray(value) ? value : String(value || '').split(',');
    return raw
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n > 0);
  };
  const normalizedRoomIds = (value) => {
    const raw = Array.isArray(value) ? value : [value];
    return [...new Set(
      raw
        .map((v) => Number(v))
        .filter((n) => Number.isInteger(n) && n > 0)
    )];
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const fetchData = async () => {
      let categoryData = [];
      let subcategoryData = [];
      try {
        const resultCategories = await getAllCategorie(token, tenant, { includeInactive: true });
        categoryData = Array.isArray(resultCategories) ? resultCategories : [];
        setCategories(categoryData);
      } catch (error) {
        console.error('Errore nel recupero delle categorie:', error);
      }
      try {
        const resultSubcategories = await getAllSottocategorie(token, tenant);
        subcategoryData = Array.isArray(resultSubcategories) ? resultSubcategories : [];
      } catch (error) {
        console.error('Errore nel recupero delle sottocategorie:', error);
      }
      setSubcategories(normalizeSubcategoryEntries(subcategoryData, categoryData));
      if (!isConferenceTenant) {
        try {
          const printers = await getConfiguredPrinters(token, tenant);
          setConfiguredPrinters(Array.isArray(printers) ? printers : []);
        } catch (error) {
          console.error('Errore nel recupero stampanti configurate:', error);
          setConfiguredPrinters([]);
        }
      } else {
        setConfiguredPrinters([]);
      }
      if (isRestaurant) {
        try {
          const roomData = await getAllStanze(token, tenant);
          setRooms(
            Array.isArray(roomData)
              ? roomData.filter((room) => room.active !== false && room.tables?.length > 0)
              : []
          );
        } catch (error) {
          console.error('Errore nel recupero delle sale:', error);
          setRooms([]);
        }
      } else {
        setRooms([]);
      }
    };
    fetchData();
  }, [open, isRestaurant, isConferenceTenant]);

  useEffect(() => {
    if (!open) return;
    resetAll();
    if (product && (mode === 'edit' || mode === 'duplicate')) {
      setId(mode === 'edit' ? (product.id || null) : null);
      setNome(product.name || '');
      setActive(mode === 'edit' ? product.active !== false : true);
      setPricePerWeight(product.pricePerWeight || false);
      setTypeOfWeight(product.typeOfWeight || '');
      setAvailableQuantity(product.availableQuantity ?? '');
      setBoardBasis(normalizeBoardBasis(product.boardBasis));
      setPrezzo(product.price === null || product.price === undefined ? '' : String(product.price));
      const productCategoryIds = Array.isArray(product.categories) && product.categories.length > 0
        ? product.categories.map((c) => Number(c.categoryId)).filter((id) => Number.isInteger(id))
        : (product.categoryId ? [Number(product.categoryId)] : []);
      setSelectedCategories(productCategoryIds);
      const firstSubcategoryId = Array.isArray(product.categories) && product.categories.length > 0
        ? (product.categories[0]?.subcategoryId ?? '')
        : (product.subcategoryId ?? '');
      setSottocategoria(firstSubcategoryId ? String(firstSubcategoryId) : '');
      setSelectedPrinters(Array.isArray(product.printerTargets) ? product.printerTargets : []);
      setRoomPrinterTargets((Array.isArray(product.roomPrinterTargets) ? product.roomPrinterTargets : [])
        .reduce((result, entry) => {
          const roomId = Number(entry?.roomId);
          if (Number.isInteger(roomId) && roomId > 0 && Array.isArray(entry?.printerTargets)) {
            result[roomId] = entry.printerTargets.map(String);
          }
          return result;
        }, {}));
      setSelectedRoomIds(normalizedRoomIds(
        Array.isArray(product.roomIds)
          ? product.roomIds
          : (Array.isArray(product.rooms) ? product.rooms.map((room) => room.id) : [])
      ));

      if (product.visibility?.length > 0) {
        const slots = product.visibility.map(v => {
          const parseTime = (timeStr) => {
            if (!timeStr) return null;
            const [h, m] = timeStr.split(':');
            const d = new Date();
            d.setHours(h, m, 0, 0);
            return d;
          };
          return {
            from: parseTime(v.startTime),
            to: parseTime(v.endTime)
          };
        });
        setTimeSlots(slots.length ? slots.map((slot) => ({
          from: slot.from || defaultTimeSlot().from,
          to: slot.to || defaultTimeSlot().to,
        })) : [defaultTimeSlot()]);
      } else {
        setTimeSlots([defaultTimeSlot()]);
      }

      setImagePreview(product.photoPath || null);
    }
  }, [open, product, mode]);

  useEffect(() => {
    if (isConferenceTenant) {
      setSottocategoria('');
      return;
    }
    if (selectedCategories.length === 0) {
      setSottocategoria('');
      return;
    }
    const primaryCategoryId = Number(selectedCategories[0]);
    const exists = subcategories.some(
      (sub) => Number(sub.id) === Number(subcategory) && Number(sub.categoryId ?? sub.category?.id) === primaryCategoryId
    );
    if (!exists) setSottocategoria('');
  }, [isConferenceTenant, selectedCategories, subcategories, subcategory]);

  function resetAll() {
    setId(null);
    setNome('');
    setActive(true);
    setPricePerWeight(false);
    setTypeOfWeight('');
    setAvailableQuantity('');
    setBoardBasis('');
    setPrezzo('');
    setSelectedCategories([]);
    setSottocategoria('');
    setSelectedPrinters([]);
    setRoomPrinterTargets({});
    setSelectedRoomIds([]);
    setTimeSlots([defaultTimeSlot()]);
    setSelectedImage(null);
    setImagePreview(null);
    setImageError('');
  }

  function handleChiudi() {
    if (onClose) {
      resetAll();
      onClose();
    }
  }

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    setImageError('');

    if (file) {
      const maxSize = 5 * 1024 * 1024;
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

      if (!allowedTypes.includes(file.type)) {
        setImageError('Formato file non supportato. Utilizzare JPG, PNG o WebP.');
        return;
      }
      if (file.size > maxSize) {
        setImageError('Il file è troppo grande. Dimensione massima: 5MB.');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTimeChange = (index, field, value) => {
    setTimeSlots(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addTimeSlot = () => setTimeSlots(prev => [...prev, defaultTimeSlot()]);

  const removeTimeSlot = (index) =>
    setTimeSlots(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [defaultTimeSlot()]);

  const formatTime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const normalizePriceInput = (value) => value.replace(',', '.');
  const serializeRoomPrinterTargets = () => {
    const allowedRoomIds = new Set(
      (selectedRoomIds.length > 0 ? selectedRoomIds : rooms.map((room) => Number(room.id))).map(Number)
    );
    return Object.entries(roomPrinterTargets)
      .map(([roomId, printerTargets]) => ({
        roomId: Number(roomId),
        printerTargets: [...new Set((Array.isArray(printerTargets) ? printerTargets : []).map(String))],
      }))
      .filter((entry) => allowedRoomIds.has(entry.roomId) && entry.printerTargets.length > 0);
  };
  const parsePrice = (value) => {
    const normalized = normalizePriceInput(String(value || '').trim());
    return Number.parseFloat(normalized);
  };

  const handleAggiungi = async () => {
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');

    const prodotto = {
      name,
      active,
      price: parsePrice(prezzo),
      availableQuantity: isConferenceTenant && String(availableQuantity).trim() !== '' ? Number(availableQuantity) : null,
      boardBasis: isConferenceTenant ? null : boardBasis || null,
      pricePerWeight: isConferenceTenant ? false : pricePerWeight,
      typeOfWeight: !isConferenceTenant && pricePerWeight ? typeOfWeight : null, // FIX: null when toggled off
      categories: normalizedCategoryIds(selectedCategories).map((catId, index) => ({
        categoryId: Number(catId),
        ...(!isConferenceTenant && index === 0 && subcategory ? { subcategoryId: Number(subcategory) } : {})
      })),
      visibility: timeSlots
        .filter(s => s.from && s.to)
        .map(s => ({
          startTime: formatTime(s.from),
          endTime: formatTime(s.to)
      })),
      printerTargets: isConferenceTenant ? [] : selectedPrinters,
      roomPrinterTargets: isRestaurant ? serializeRoomPrinterTargets() : [],
      ...(isRestaurant ? { roomIds: selectedRoomIds } : {}),
    };

    try {
      if (selectedImage) {
        await creaProdottoMultipart(prodotto, selectedImage, token, tenant);
      } else {
        await creaProdotto(prodotto, token, tenant);
      }
      window.dispatchEvent(new CustomEvent('prodottoCreato'));
      resetAll();
      handleChiudi();
    } catch (error) {
      handleAlert(t('reservations_details.error') + ': ' + error.message);
    }
  };

const handleAggiorna = async () => {
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');

    const prodotto = {
      id,
      name,
      active,
      price: parsePrice(prezzo),
      availableQuantity: isConferenceTenant && String(availableQuantity).trim() !== '' ? Number(availableQuantity) : null,
      boardBasis: isConferenceTenant ? null : boardBasis || null,
      pricePerWeight: isConferenceTenant ? false : pricePerWeight,
      typeOfWeight: !isConferenceTenant && pricePerWeight ? typeOfWeight : null, // FIX: key name + null when off
      categories: normalizedCategoryIds(selectedCategories).map((catId, index) => ({
        categoryId: Number(catId),
        ...(!isConferenceTenant && index === 0 && subcategory ? { subcategoryId: Number(subcategory) } : {})
      })),
      visibility: timeSlots
        .filter(s => s.from && s.to)
        .map(s => ({
          startTime: formatTime(s.from),
          endTime: formatTime(s.to)
      })),
      printerTargets: isConferenceTenant ? [] : selectedPrinters,
      roomPrinterTargets: isRestaurant ? serializeRoomPrinterTargets() : [],
      ...(isRestaurant ? { roomIds: selectedRoomIds } : {}),
      photoPath: !selectedImage && !imagePreview ? '' : undefined,
    };

    try {
      if (selectedImage || (!selectedImage && !imagePreview)) {
        await aggiornaProdottoMultipart(prodotto.id, prodotto, selectedImage, token, tenant);
      } else {
        await aggiornaProdotto(prodotto.id, prodotto, token, tenant);
      }
      window.dispatchEvent(new CustomEvent('prodottoAggiornato'));
      resetAll();
      handleChiudi();
    } catch (error) {
      handleAlert(t('reservations_details.error') + ': ' + error.message);
    }
  };

  function handleAlert(msg, type) {
    showSnackbar(msg, type);
  }

  return (
    <div>
      <Modal open={open} onClose={handleChiudi}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" gutterBottom>
            {mode === 'edit' ? t('edit_product') : t('new_product')}
          </Typography>

          <Box mb={2} textAlign="center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <Avatar
                src={imagePreview}
                sx={{
                  width: 120, height: 120, bgcolor: 'grey.200',
                  cursor: 'pointer', border: '2px dashed',
                  borderColor: imageError ? 'error.main' : 'grey.400'
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {!imagePreview && <PhotoCamera sx={{ fontSize: 40, color: 'grey.500' }} />}
              </Avatar>

              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoCamera />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? t('changeImage') : t('addImage')}
                </Button>
                {imagePreview && (
                  <IconButton size="small" color="error" onClick={handleRemoveImage}>
                    <Delete />
                  </IconButton>
                )}
              </Box>

              {imageError && (
                <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                  {imageError}
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary">
                {t('formats')}: JPG, PNG (max 5MB)
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            label={t('product_name')}
            variant="outlined"
            margin="normal"
            value={name}
            onChange={(e) => setNome(e.target.value)}
          />

          {mode === 'edit' && (
            <FormControlLabel
              control={<Switch checked={active} onChange={(event) => setActive(event.target.checked)} />}
              label={active ? 'Articolo attivo' : 'Articolo disattivato'}
              sx={{ mt: 1 }}
            />
          )}

          {/* FIX: MenuItem value is String so it matches the category state (also a String) */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="category-label">{t('category')}</InputLabel>
            <Select
              labelId="category-label"
              multiple
              value={selectedCategories}
              label={t('category')}
              onChange={(e) => setSelectedCategories(normalizedCategoryIds(e.target.value))}
              renderValue={(selected) =>
                categories
                  .filter((cat) => selected.includes(cat.id))
                  .map((cat) => cat.name)
                  .join(', ')
              }
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Checkbox checked={selectedCategories.includes(cat.id)} />
                  <ListItemText primary={cat.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!isConferenceTenant && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="subcategory-label">{t('subcategory')}</InputLabel>
              <Select
                labelId="subcategory-label"
                value={subcategory}
                label={t('subcategory')}
                onChange={(e) => setSottocategoria(e.target.value)}
              >
                <MenuItem value="">{t('common.none', { defaultValue: '-' })}</MenuItem>
                {subcategories
                  .filter((sub) => Number(sub.categoryId ?? sub.category?.id) === Number(selectedCategories[0]))
                  .map((sub) => (
                    <MenuItem key={sub.id} value={String(sub.id)}>{sub.name}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {isRestaurant && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="product-room-label">{t('product_room')}</InputLabel>
              <Select
                labelId="product-room-label"
                multiple
                value={selectedRoomIds}
                label={t('product_room')}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRoomIds(
                    Array.isArray(value) && value.includes('')
                      ? []
                      : normalizedRoomIds(value)
                  );
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) return t('product_room_all');
                  return rooms
                    .filter((room) => selected.includes(Number(room.id)))
                    .map((room) => room.name)
                    .join(', ');
                }}
              >
                <MenuItem value="">
                  <Checkbox checked={selectedRoomIds.length === 0} />
                  <ListItemText primary={t('product_room_all')} />
                </MenuItem>
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={Number(room.id)}>
                    <Checkbox checked={selectedRoomIds.includes(Number(room.id))} />
                    <ListItemText primary={room.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isRestaurant && configuredPrinters.length > 0 && (
            <Box mt={1} mb={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                Stampanti specifiche per sala
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Se non selezioni nulla, la sala usa le stampanti generali dell'articolo.
              </Typography>
              {(selectedRoomIds.length > 0
                ? rooms.filter((room) => selectedRoomIds.includes(Number(room.id)))
                : rooms
              ).map((room) => {
                const roomId = Number(room.id);
                const selected = roomPrinterTargets[roomId] || [];
                return (
                  <FormControl fullWidth margin="dense" key={roomId}>
                    <InputLabel id={`room-printers-${roomId}`}>{room.name}</InputLabel>
                    <Select
                      labelId={`room-printers-${roomId}`}
                      multiple
                      value={selected}
                      label={room.name}
                      onChange={(event) => {
                        const value = Array.isArray(event.target.value)
                          ? event.target.value.map(String)
                          : [String(event.target.value)];
                        setRoomPrinterTargets((current) => ({ ...current, [roomId]: value }));
                      }}
                      renderValue={(values) => values.join(', ')}
                    >
                      {configuredPrinters.map((printer) => (
                        <MenuItem key={printer.id} value={printer.name}>
                          <Checkbox checked={selected.includes(printer.name)} />
                          <ListItemText primary={printer.name} secondary={printer.ip || ''} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              })}
            </Box>
          )}

          <TextField
            fullWidth
            label={t('price')}
            type="text"
            inputMode="decimal"
            inputProps={{ step: '0.01', min: '0' }}
            variant="outlined"
            margin="normal"
            value={prezzo}
            onChange={(e) => {
              const value = normalizePriceInput(e.target.value);
              if (/^\d*\.?\d{0,2}$/.test(value)) setPrezzo(value);
            }}
            onWheel={(e) => e.target.blur()}
          />

          {isConferenceTenant && (
            <TextField
              fullWidth
              label={t('qta', { defaultValue: 'Quantità' })}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              variant="outlined"
              margin="normal"
              value={availableQuantity}
              onChange={(e) => setAvailableQuantity(e.target.value)}
              helperText={t('availableQuantityHelp', { defaultValue: 'Quantità totale disponibile per giorno' })}
            />
          )}

          {!isConferenceTenant && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="board-basis-label">{t('boardBasis.label', { defaultValue: 'Board basis' })}</InputLabel>
              <Select
                labelId="board-basis-label"
                value={boardBasis}
                label={t('boardBasis.label', { defaultValue: 'Board basis' })}
                onChange={(e) => setBoardBasis(normalizeBoardBasis(e.target.value))}
              >
                {BOARD_BASIS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'none'} value={option.value}>
                    {option.value ? option.label : t('boardBasis.none', { defaultValue: option.label })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!isConferenceTenant && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="printer-targets-label">{t("product_printer_targets")}</InputLabel>
              <Select
                labelId="printer-targets-label"
                multiple
                value={selectedPrinters}
                label={t("product_printer_targets")}
                onChange={(e) => {
                  const value = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                  setSelectedPrinters(value.map((entry) => String(entry)));
                }}
                renderValue={(selected) => selected.join(', ')}
              >
                {configuredPrinters.map((printer) => (
                  <MenuItem key={printer.id} value={printer.name}>
                    <Checkbox checked={selectedPrinters.includes(printer.name)} />
                    <ListItemText primary={printer.name} secondary={printer.ip || ''} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!isConferenceTenant && (
            <Box display="flex" gap={2} flexDirection="column">
            <FormControlLabel
              control={
                <Switch
                  checked={pricePerWeight}
                  onChange={(e) => {
                    setPricePerWeight(e.target.checked);
                    if (!e.target.checked) setTypeOfWeight('');
                  }}
                />
              }
              label={t('price_per_weight')}
            />
            {pricePerWeight && (
              <FormControl fullWidth>
                <InputLabel id="weight_unit">{t('weight_unit')}</InputLabel>
                <Select
                  labelId="weight_unit"
                  value={typeOfWeight}
                  label={t('weight_unit')}
                  onChange={(e) => setTypeOfWeight(e.target.value)}
                >
                  <MenuItem value={weightEnum.KILOGRAM}>€/Kg</MenuItem>
                  <MenuItem value={weightEnum.HECTOGRAM}>€/hg</MenuItem>
                </Select>
              </FormControl>
            )}
            </Box>
          )}

          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {timeSlots.map((slot, i) => (
              <Box key={i} display="flex" gap={2} alignItems="center">
                <TimePicker24Field
                  value={slot.from ?? new Date(new Date().setHours(0, 0, 0, 0))}
                  onChange={(v) => handleTimeChange(i, 'from', v)}
                  label={t('fromTime')}
                  ampm={false}
                  format="HH:mm"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                        autoComplete: 'off',
                        'aria-autocomplete': 'none',
                      }}
                      InputProps={{
                        ...params.InputProps,
                        autoComplete: 'off',
                      }}
                    />
                  )}
                />
                <TimePicker24Field
                  value={slot.to ?? new Date(new Date().setHours(23, 59, 0, 0))}
                  onChange={(v) => handleTimeChange(i, 'to', v)}
                  label={t('toTime')}
                  ampm={false}
                  format="HH:mm"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                        autoComplete: 'off',
                        'aria-autocomplete': 'none',
                      }}
                      InputProps={{
                        ...params.InputProps,
                        autoComplete: 'off',
                      }}
                    />
                  )}
                />
                <IconButton
                  color="error"
                  onClick={() => removeTimeSlot(i)}
                  disabled={timeSlots.length === 1}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <IconButton onClick={addTimeSlot}><AddIcon /></IconButton>
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button onClick={handleChiudi} sx={{ mr: 1 }}>
              {t('cancel_button')}
            </Button>
            <Button
              variant="contained"
              onClick={mode === 'edit' ? handleAggiorna : handleAggiungi}
            >
              {mode === 'edit' ? t('update_button') : t('add_button')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

export default ModalProdotto;
